import OpenSeadragon from "openseadragon";
import { useCallback, useRef } from "react";

import { ViewerMode } from "custom-models/ViewerTypes";

import OSDClient from "../OSDClient";
import OSDClientEffectiveMagnification from "../OSDClientEffectiveMagnification";

type ViewerClient = OSDClient | OSDClientEffectiveMagnification;

interface UseSpaceMouseOptions {
  viewerRef: React.MutableRefObject<ViewerClient | null>;
  viewerMode: ViewerMode;
  enabled?: boolean;
  panSpeed?: number;
}

export function useSpaceMouse({
  viewerRef,
  viewerMode,
  enabled = true,
  panSpeed = 40,
}: UseSpaceMouseOptions) {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled && viewerMode === ViewerMode.PAN_ZOOM;

  const handlePan = useCallback(
    (deltaX: number, deltaY: number) => {
      if (!enabledRef.current) return;

      const viewer = viewerRef.current?.viewer;
      if (!viewer?.viewport) return;

      const pixelDelta = new OpenSeadragon.Point(
        deltaX * panSpeed,
        deltaY * panSpeed
      );

      const viewportDelta = viewer.viewport.deltaPointsFromPixels(pixelDelta);
      viewer.viewport.panBy(viewportDelta, false);
    },
    [panSpeed, viewerRef]
  );

  const handleZoom = useCallback(
    (direction: number) => {
      if (!enabledRef.current) return;

      const client = viewerRef.current;
      if (!client) return;

      if (direction < 0) {
        client.doZoomIn();
      } else {
        client.doZoomOut();
      }
    },
    [viewerRef]
  );

  const handleRotateLeft = useCallback(() => {
    if (!enabledRef.current) return;

    const client = viewerRef.current;
    if (!client) return;

    client.doRotate(-10);
  }, [viewerRef]);

  const handleRotateRight = useCallback(() => {
    if (!enabledRef.current) return;

    const client = viewerRef.current;
    if (!client) return;

    client.doRotate(10);
  }, [viewerRef]);

  return {
    handlers: {
      onPan: handlePan,
      onZoom: handleZoom,
      onRotateLeft: handleRotateLeft,
      onRotateRight: handleRotateRight,
    },
  };
}
